import { Injectable } from "@angular/core";
import { Observable, Subject } from "rxjs";
import { ApiService } from "src/app/services/api.service";
import { environment } from "src/environments/environment";
import { FilterService } from "./filter.service";
import { UserActivityService } from "./useractivity.service";
import { NavigationService } from "./navigation.service";
import { GridStateService } from "./grid-state.service";
import { RequestCancelService } from "./request-cancel.service";
import { PhiSecurityService } from "./phi-security.service";

// The EMD Wholesaler Dashboard is a static prototype screen. It is not a module
// of its own: it lives as an extra dashboard tile under the existing Sales
// module, and follows the normal module → dashboard → page flow from there.
// Dashboard access does not return this row yet, so it is injected in
// DashboardComponent.getDashboardData and routed by dashboard_key in openPage.
// Remove both once the API returns it.
export const SALES_MODULE_KEY = 100114;
export const EMD_DASHBOARD_KEY = "20250101000118";
export const EMD_DASHBOARD_NM = "Wholesaler Dashboard";

// AI module (Scraii AI) - gates features like the floating chatbot to users
// whose module access list includes this key with access_flg == "Y".
export const AI_MODULE_KEY = 100119;

// Visit Planner's dashboard key: its tile is in FRM_CASE_MANAGEMENT_DASHBOARDS
// below, and the static registry still mounts it for old `:module/static` links.
export const VISIT_PLANNER_DASHBOARD_KEY = "20261005131500";
export const VISIT_PLANNER_DASHBOARD_NM = "Visit Planner";
export const CASE_MANAGEMENT_MODULE_NM = "Case Management";

// The FRM module carries Case Management's live-API screens as dashboard tiles.
// get-dashboard-access has no rows for them, so DashboardComponent injects one per
// entry, in this order, while FRM is open, and openPage sends each to its screen in
// the lazy CaseManagementModule, which the dashboard routes mount at
// `:module/case-management` (as du-admin does). Keys are minted once in the API's
// YYYYMMDDHHmmss shape and never change; drop an entry once the API returns its row.
export const FRM_MODULE_KEY = 100110;

export interface CaseManagementDashboard {
  key: string;
  name: string;
  /** Path under `:module/case-management` (case-management-routing.module.ts). */
  route: string;
  /** The tile's picture, under src/assets/svgIcons. */
  image: string;
}

export const FRM_CASE_MANAGEMENT_DASHBOARDS: CaseManagementDashboard[] = [
  // The case board; Create case and the case page open from it.
  { key: "20261027130213", name: "Case Management", route: "cases", image: "case_management.svg" },
  { key: VISIT_PLANNER_DASHBOARD_KEY, name: VISIT_PLANNER_DASHBOARD_NM, route: "visit-planner", image: "visit_planner.svg" },
  { key: "20261027130214", name: "Workflow Designer", route: "workflow-designer", image: "workflow_designer.svg" },
  // CFG-01, the configuration admin's only area.
  { key: "20261027130215", name: "Case Types & Fields", route: "admin/case-types", image: "case_type&fields.svg" },
];

// WorkFlow Designer — static sidebar entry when getModuleAccess has no row.
// Routed like other Direct modules (100116/100117). Remove once the API
// returns this module for the tenant.
export const WORKFLOW_DESIGNER_MODULE_KEY = 100121;
export const WORKFLOW_DESIGNER_MODULE_NM = "WorkFlow Designer";

// FRMCRM — static sidebar entry when getModuleAccess has no row. It has no
// dashboards: a click opens Case Management's landing page directly, at
// `dashboard/FRMCRM/case-management`. 100122 is the next key after WorkFlow
// Designer's; remove the entry once the API returns this module for the tenant.
export const FRMCRM_MODULE_KEY = 100122;
export const FRMCRM_MODULE_NM = "FRMCRM";

// Tile artwork for the injected dashboard row. Points at the same shared
// svgIcons folder the real get-dashboard-access rows use; there is no
// environment key for this bucket, so it is spelled out here exactly as the
// existing hardcoded 100108 row in DashboardComponent does. Reusing
// patientList.svg until EMD gets dedicated artwork.
export const EMD_ASSET_BUCKET = "dustage-analytics-ui-s3";
export const EMD_ASSET_BASE = `https://${EMD_ASSET_BUCKET}.s3.amazonaws.com/assets/svgIcons`;

@Injectable({
  providedIn: "root",
})
export class DashboardService {
  public apiBaseUrl: any;
  module: any;
  moduleKey: any;
  dashboardName: any;

  sidebarSizeEventChanges = new Subject<any>();

  linkPageNavigation: any = {};
  getPageKey: any = [];
  dashboardKey: any;
  dashboardDetailsOfNotes: any;

  private _menuItems: any;
  // Fires whenever menuItems is (re)populated, so consumers that need to react
  // to module access becoming available (e.g. floating chatbot visibility)
  // don't have to poll a plain property.
  private readonly menuItemsUpdatedSource = new Subject<void>();
  readonly menuItemsUpdated$ = this.menuItemsUpdatedSource.asObservable();

  get menuItems(): any {
    return this._menuItems;
  }
  set menuItems(value: any) {
    this._menuItems = value;
    this.menuItemsUpdatedSource.next();
  }

  constructor(
    private apiService: ApiService,
    private filterService: FilterService,
    private userActivityService: UserActivityService,
    private navigationService: NavigationService,
    private gridStateService: GridStateService,
    private requestCancelService: RequestCancelService,
    private phiSecurityService: PhiSecurityService
  ) {
    this.apiBaseUrl = environment.apiBaseUrl;
  }

  getModuleDetails() {
    return this.module;
  }
  setModuleDetails(module: any) {
    this.module = module;
  }

  // Same access_flg == "Y" + module_key match already used inline in
  // DashboardComponent and BookmarkViewComponent, centralized so other
  // consumers (e.g. floating chatbot visibility) don't duplicate it.
  hasModuleAccess(moduleKey: number | string): boolean {
    if (!Array.isArray(this.menuItems)) {
      return false;
    }
    return this.menuItems.some(
      (item: any) =>
        item?.access_flg === "Y" && Number(item?.module_key) === Number(moduleKey)
    );
  }

  /**
   * Adds client-side modules that getModuleAccess does not yet return: FRMCRM
   * first in the sidebar, WorkFlow Designer last. Idempotent — skips each if its
   * name or key is present (the API's row then keeps the API's position).
   */
  ensureStaticDirectModules(modules: any[]): any[] {
    if (!Array.isArray(modules) || modules.length === 0) {
      return modules;
    }
    const has = (key: number, name: string) =>
      modules.some((m: any) => m?.module_nm === name || Number(m?.module_key) === key);
    const clientKey = modules[0]?.client_key;
    const first: any[] = [];
    const last: any[] = [];

    if (!has(WORKFLOW_DESIGNER_MODULE_KEY, WORKFLOW_DESIGNER_MODULE_NM)) {
      last.push({
        module_key: WORKFLOW_DESIGNER_MODULE_KEY,
        module_nm: WORKFLOW_DESIGNER_MODULE_NM,
        client_key: clientKey,
        access_flg: "Y",
        hide_flg: "N",
        active_image: "assets/svgIcons/settings.svg",
        hover_image: "assets/svgIcons/settings.svg",
      });
    }
    if (!has(FRMCRM_MODULE_KEY, FRMCRM_MODULE_NM)) {
      first.push({
        module_key: FRMCRM_MODULE_KEY,
        module_nm: FRMCRM_MODULE_NM,
        client_key: clientKey,
        access_flg: "Y",
        hide_flg: "N",
        active_image: "assets/svgIcons/module_frm.svg",
        hover_image: "assets/svgIcons/module_frm.svg",
      });
    }
    return first.length || last.length ? [...first, ...modules, ...last] : modules;
  }

  /** FRMCRM by key, or by name once the API returns its row under a key of its own. */
  isFrmCrmModule(moduleKey: any, moduleName: any): boolean {
    return Number(moduleKey) === FRMCRM_MODULE_KEY || moduleName === FRMCRM_MODULE_NM;
  }

  /**
   * Opens FRMCRM on Case Management's landing page, as DU Admin's Case Management
   * card does: cl_key / md_key only, and the topnav lists FRMCRM with the modules
   * it shows as a page (back arrow, the shell's title). `replaceUrl` when
   * redirecting from `dashboard/FRMCRM` (the topnav logo), so Back does not land
   * there and bounce straight back.
   */
  openFrmCrm(clientKey: any, moduleKey: any, moduleName: string, replaceUrl = false) {
    sessionStorage.setItem("navbar_page_nm", JSON.stringify({ dashboard_nm: CASE_MANAGEMENT_MODULE_NM }));
    this.navigationService.routerNavigate(
      `dashboard/${moduleName}/case-management`,
      { cl_key: clientKey, md_key: moduleKey },
      { replaceUrl }
    );
  }

  getModuleAccess(obj: any) {
    let body = {
      username: obj.username,
      client_key: obj.client_key,
    };
    let url = `api/get-module-access`;
    return this.apiService.post(url, body);
  }

  getDashboardAccess(obj: any) {
    let body = {
      username: obj.username,
      client_key: obj.client_key,
      module_key: obj.module_key,
    };
    let url = `api/get-dashboard-access`;
    return this.apiService.post(url, body);
  }

  getPageAccess(obj: any) {
    let url = `api/get-page-access`;
    return this.apiService.post(url, obj);
  }

  getPageDetails(body: any) {
    let url = `kpi/get-json-file-data`;
    return this.apiService.post(url, body);
  }

  getRefreshedLastDate(obj: any) {
    if (!obj["brand_key"]) return new Observable((ob) => ob.next(undefined));
    let url = `kpi/get-last-refreshed-date`;
    return this.apiService.post(url, obj);
  }

  getBrandBasedExceptions(obj: any) {
    let url = `kpi/get-filter-exceptions`;
    return this.apiService.post(url, obj);
  }

  getAllUserForImpersonate(data:any) {
    let url = `api/get-impersonation-users`;
    return this.apiService.post(url, data);
    // if(environment.typeOfEnv=='DEV') {
    // } else {
    //   let url = `api/get-all-users`;
    //   return this.apiService.get(url); 
    // }
  }

  getAllUser() {
    let url = `api/get-all-users`;
    return this.apiService.get(url);
  }

  userMenuModuleHandle(usermenu: any, item: any) {
    this.filterService.phiChecked = false;
    this.phiSecurityService.removePHIWarningOverlay();
    this.phiSecurityService.disableRestrictions();

    if (usermenu) {
      this.filterService.refreshData = false;
      this.filterService.brandCarry = false;

      // Filter reset in Nav track
      this.filterService.resetFilterAndPayloadInNavDetails();
      sessionStorage.removeItem("source_patient_id");
      let userStateData: any = this.userActivityService.getData();
      let key = Object.keys(userStateData.selectedFilters).length;
      if (key > 0) {
        this.filterService.resetFilter();
      }

      if (userStateData.selectedType != "") {
        this.userActivityService.setData({ selectedType: "" });
      }

      this.moduleKey = item.module_key;
      this.userActivityService.hierarchicalGridState = [];

      sessionStorage.removeItem("brandKeyForFilters");
      // The static dashboards' half of the user state, alongside the portal's
      // own above: DPM-Static and DEM-Static keep their dynamic filters
      // (Brand) there rather than in a filter widget, so this is where their
      // selection is dropped on a module switch too.
      this.userActivityService.clearStaticFilters();
      sessionStorage.removeItem("tbl_name_suffix");

      sessionStorage.removeItem("tbl_name_suffix_non_frm");
      // sessionStorage.removeItem("serverSidegridState");
      this.gridStateService.clearAll();

      // Select module
      this.setModuleDetails(item);
      sessionStorage.setItem("module", JSON.stringify(item));

      let directKeys = [100116, 100117, WORKFLOW_DESIGNER_MODULE_KEY];
      let duAdminKeys = [100118];
      const isWorkflowDesigner =
        Number(item.module_key) === WORKFLOW_DESIGNER_MODULE_KEY ||
        item.module_nm === WORKFLOW_DESIGNER_MODULE_NM;

      if (duAdminKeys.includes(Number(item.module_key))) {
        let query = {
          cl_key: item.client_key,
          md_key: item.module_key,
        };
        this.navigationService.routerNavigate(
          `du-admin/${item.module_nm}`,
          query
        );
      } else if (directKeys.includes(Number(item.module_key)) || isWorkflowDesigner) {
        let query = {
          cl_key: item.client_key,
          md_key: item.module_key,
        };
        this.navigationService.routerNavigate(
          `direct/${item.module_nm}`,
          query
        );
      } else if (this.isFrmCrmModule(item.module_key, item.module_nm)) {
        this.openFrmCrm(item.client_key, item.module_key, item.module_nm);
      } else {
        let query = {
          cl_key: item.client_key,
          md_key: item.module_key,
        };
        this.navigationService.routerNavigate(
          `dashboard/${item.module_nm}`,
          query
        );
      }

      // Cancel all pending API calls when switching modules
      this.requestCancelService.cancelAllRequests();
      this.filterService.brandSelectWithBackNavigation = false;
    }
  }
}
